import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { GO_SERVICE_QUERY_KEYS } from "../queryKeys";
import { getServiceFeedbackForClient } from "../services/goHireService";
import { ServiceFeedback } from "../../types/service.type";
import { ServiceFeedbackResponse } from "../types/serviceFeedback.types";

const FEEDBACK_PAGE_LIMIT = 10;

interface UseServiceFeedbackForClientParams {
  userId: number;
  limit?: number;
  page?: number;
}

export const useServiceFeedbackForClientInfinite = ({
  userId,
  limit = FEEDBACK_PAGE_LIMIT,
}: UseServiceFeedbackForClientParams) => {
  return useInfiniteQuery<
    ServiceFeedbackResponse,
    Error,
    { items: ServiceFeedback[]; meta: ServiceFeedbackResponse["meta"] }
  >({
    queryKey: [...GO_SERVICE_QUERY_KEYS.serviceFeedbackForClient(userId), { limit }],
    queryFn: ({ pageParam }) => {
      const currentPage = (pageParam as number | undefined) ?? 0;
      return getServiceFeedbackForClient(userId, limit, currentPage * limit);
    },
    getNextPageParam: (lastPage, allPages) => {
      const fetchedCount = allPages.length * limit;
      return fetchedCount < lastPage.meta.totalItems ? allPages.length : undefined;
    },
    select: (data) => ({
      items: data.pages.flatMap((page) => page.items),
      meta: data.pages[0].meta,
    }),
    initialPageParam: 0,
    enabled: !!userId,
  });
};

export const useServiceFeedbackForClientPaginated = ({
  userId,
  limit = FEEDBACK_PAGE_LIMIT,
  page = 0,
  enabled,
}: UseServiceFeedbackForClientParams & { enabled?: boolean }) => {
  return useQuery<ServiceFeedbackResponse, Error, ServiceFeedback[]>({
    queryKey: [...GO_SERVICE_QUERY_KEYS.serviceFeedbackForClient(userId), { limit, page }],
    queryFn: () => getServiceFeedbackForClient(userId, limit, page * limit),
    staleTime: 1000 * 60 * 5, // 5 minutes
    select: (data) => data.items,
    enabled: enabled !== undefined ? enabled && !!userId : !!userId,
  });
};
